
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/base/Navigation';
import Footer from '../../components/feature/Footer';
import BookingCalendar from '../../components/feature/BookingCalendar';

type FormData = {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  industry: string;
  companySize: string;
  message: string;
};

const useScrollAnimation = () => {
  const [visibleElements, setVisibleElements] = useState<Set<string>>(new Set());
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target instanceof HTMLElement) {
            setVisibleElements((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const elements = document.querySelectorAll<HTMLElement>('[data-animate]');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return visibleElements;
};

export default function ContactPage() {
  const visibleElements = useScrollAnimation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const heroImages = [
    'https://public.readdy.ai/ai/img_res/87718873be57b8a6d589b1be3aa684ec.jpg',
    'https://readdy.ai/api/search-image?query=Bright%20contemporary%20coworking%20space%20with%20entrepreneurs%20collaborating%20at%20wooden%20communal%20tables%20exposed%20brick%20walls%20industrial%20pendant%20lighting%20large%20windows%20with%20natural%20light%20real%20candid%20business%20photography%20authentic%20startup%20environment%20professional%20workspace%20with%20notebooks%20and%20devices&width=1920&height=600&seq=contact-hero-cowork-2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Elegant%20modern%20reception%20area%20with%20marble%20desk%20comfortable%20designer%20seating%20area%20large%20floor%20to%20ceiling%20windows%20flooding%20space%20with%20natural%20light%20indoor%20tropical%20plants%20contemporary%20furniture%20real%20commercial%20interior%20photography%20welcoming%20professional%20business%20entrance%20high%20end%20corporate%20space&width=1920&height=600&seq=contact-hero-lobby-3&orientation=landscape'
  ];

  useEffect(() => {
    const changeImage = () => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
        setIsTransitioning(false);
      }, 500);
    };
    const intervalId = setInterval(changeImage, 5000);
    return () => clearInterval(intervalId);
  }, [heroImages.length]);

  const getAnimationClass = (elementId: string, baseClass: string = '') => {
    const isVisible = visibleElements.has(elementId);
    return `${baseClass} transition-all duration-1000 ease-out ${
      isVisible
        ? 'opacity-100 translate-y-0 scale-100'
        : 'opacity-0 translate-y-8 scale-95'
    }`;
  };

  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    industry: '',
    companySize: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });
      const response = await fetch(
        'https://readdy.ai/api/form/d5qmbo8tu5df1co4dang',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formBody
        }
      );
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          website: '',
          industry: '',
          companySize: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: 'ri-map-pin-line', label: 'Location', value: 'Buffalo, NY\nUnited States' },
    { icon: 'ri-phone-line', label: 'Phone', value: '(716) 939-5685' },
    { icon: 'ri-mail-line', label: 'Email', value: 'Sales@ClearPathIntelligence.net' }
  ];

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-500/20 via-indigo-600/10 to-transparent"></div>
        <div
          className="absolute bottom-1/4 left-0 w-96 h-96 bg-gradient-to-tr from-indigo-600/30 to-transparent blur-3xl animate-pulse"
          style={{ animationDelay: '2s' }}
        ></div>
      </div>

      <Navigation />

      {/* Hero Section */}
      <section className="pt-24 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                index === currentImageIndex
                  ? 'opacity-100 scale-100 blur-0'
                  : 'opacity-0 scale-110 blur-sm'
              }`}
            >
              <img
                src={image}
                alt={`Contact hero ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          <div
            className={`absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-slate-950 transition-opacity duration-1000 ${
              isTransitioning ? 'opacity-80' : 'opacity-70'
            }`}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-indigo-900/20"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div
            id="contact-hero"
            data-animate
            className={getAnimationClass('contact-hero', 'text-center space-y-6')}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white font-space">
              Let&apos;s <span className="text-indigo-400">Connect</span>
            </h1>
            <p className="text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
              Ready to transform your business with intelligent web solutions? We&apos;d love to hear from you. Reach out and let&apos;s discuss your vision.
            </p>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                index === currentImageIndex
                  ? 'w-8 h-2 bg-indigo-400'
                  : 'w-2 h-2 bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-slate-900 relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {contactInfo.map((item, index) => (
              <div
                key={index}
                id={`info-card-${index}`}
                data-animate
                className={getAnimationClass(
                  `info-card-${index}`,
                  'bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50 hover:border-indigo-500/50 transition-all duration-300 text-center group hover:-translate-y-1'
                )}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-indigo-500/30 transition-colors">
                  <i className={`${item.icon} text-indigo-400 text-2xl`}></i>
                </div>
                <h3 className="text-white font-semibold mb-2">{item.label}</h3>
                <p className="text-slate-300 text-sm whitespace-pre-line">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Booking Section */}
      <section className="py-20 bg-slate-800 relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div
              id="contact-form-section"
              data-animate
              className={getAnimationClass('contact-form-section', '')}
            >
              <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-8 lg:p-10 border border-slate-700/50 shadow-lg shadow-indigo-500/5">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-white mb-3 font-space">
                    Tell Us About Your <span className="text-indigo-400">Project</span>
                  </h2>
                  <p className="text-slate-400">
                    Share your details and we&apos;ll get back to you within 24 hours.
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-check-circle-fill text-emerald-400 text-xl mr-3"></i>
                      <span className="text-emerald-300 font-medium">
                        Thank you! Your message has been sent successfully.
                      </span>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-error-warning-fill text-red-400 text-xl mr-3"></i>
                      <span className="text-red-300 font-medium">
                        Sorry, there was an error. Please try again.
                      </span>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                  <div className="grid md:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label
                        htmlFor="companyName"
                        className="block text-sm font-medium text-slate-300 mb-2"
                      >
                        Company Name *
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 text-sm bg-slate-800/80 border border-slate-600/50 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        placeholder="Your company name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contactName"
                        className="block text-sm font-medium text-slate-300 mb-2"
                      >
                        Contact Name *
                      </label>
                      <input
                        type="text"
                        id="contactName"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 text-sm bg-slate-800/80 border border-slate-600/50 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        placeholder="Your full name"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-slate-300 mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 text-sm bg-slate-800/80 border border-slate-600/50 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        placeholder="your.email@company.com"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-slate-300 mb-2"
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 text-sm bg-slate-800/80 border border-slate-600/50 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label
                        htmlFor="website"
                        className="block text-sm font-medium text-slate-300 mb-2"
                      >
                        Company Website
                      </label>
                      <input
                        type="url"
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 text-sm bg-slate-800/80 border border-slate-600/50 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        placeholder="https://yourcompany.com"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="industry"
                        className="block text-sm font-medium text-slate-300 mb-2"
                      >
                        Industry
                      </label>
                      <select
                        id="industry"
                        name="industry"
                        value={formData.industry}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 text-sm bg-slate-800/80 border border-slate-600/50 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      >
                        <option value="" className="bg-slate-800">
                          Select your industry
                        </option>
                        <option value="Technology" className="bg-slate-800">
                          Technology
                        </option>
                        <option value="Healthcare" className="bg-slate-800">
                          Healthcare
                        </option>
                        <option value="Finance" className="bg-slate-800">
                          Finance
                        </option>
                        <option value="Manufacturing" className="bg-slate-800">
                          Manufacturing
                        </option>
                        <option value="Retail" className="bg-slate-800">
                          Retail
                        </option>
                        <option value="Education" className="bg-slate-800">
                          Education
                        </option>
                        <option value="Real Estate" className="bg-slate-800">
                          Real Estate
                        </option>
                        <option value="Professional Services" className="bg-slate-800">
                          Professional Services
                        </option>
                        <option value="Non-Profit" className="bg-slate-800">
                          Non-Profit
                        </option>
                        <option value="Other" className="bg-slate-800">
                          Other
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-5">
                    <label
                      htmlFor="companySize"
                      className="block text-sm font-medium text-slate-300 mb-2"
                    >
                      Company Size
                    </label>
                    <select
                      id="companySize"
                      name="companySize"
                      value={formData.companySize}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 text-sm bg-slate-800/80 border border-slate-600/50 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    >
                      <option value="" className="bg-slate-800">
                        Select company size
                      </option>
                      <option value="1-10 employees" className="bg-slate-800">
                        1-10 employees
                      </option>
                      <option value="11-50 employees" className="bg-slate-800">
                        11-50 employees
                      </option>
                      <option value="51-200 employees" className="bg-slate-800">
                        51-200 employees
                      </option>
                      <option value="201-500 employees" className="bg-slate-800">
                        201-500 employees
                      </option>
                      <option value="501+ employees" className="bg-slate-800">
                        501+ employees
                      </option>
                    </select>
                  </div>

                  <div className="mb-6">
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-slate-300 mb-2"
                    >
                      Project Details *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 text-sm bg-slate-800/80 border border-slate-600/50 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-none"
                      placeholder="Tell us about your project needs, goals, and how we can help your business grow..."
                    ></textarea>
                    <div className="mt-2 text-right text-xs text-slate-500">
                      {formData.message.length}/500 characters
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || formData.message.length > 500}
                    className="w-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-semibold py-4 px-8 rounded-lg hover:from-indigo-600 hover:to-blue-700 focus:ring-4 focus:ring-indigo-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-lg shadow-indigo-500/25 hover:-translate-y-0.5 cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin text-xl mr-2"></i>
                        Sending Message...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <i className="ri-send-plane-fill text-xl mr-2"></i>
                        Send Message
                      </span>
                    )}
                  </button>

                  <p className="text-center text-sm text-slate-500 mt-4">
                    We&apos;ll review your information and get back to you within 24 hours.
                  </p>
                </form>
              </div>
            </div>

            {/* Right Side - Quick Info + CTA */}
            <div
              id="contact-sidebar"
              data-animate
              className={getAnimationClass('contact-sidebar', 'space-y-8')}
              style={{ animationDelay: '0.2s' }}
            >
              {/* Why Choose Us */}
              <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50">
                <h3 className="text-2xl font-bold text-white mb-6 font-space">
                  Why Work With <span className="text-indigo-400">Us</span>?
                </h3>
                <div className="space-y-5">
                  {[
                    {
                      icon: 'ri-timer-line',
                      title: 'Fast Turnaround',
                      desc: 'Mock website ready in 3-5 business days'
                    },
                    { icon: 'ri-robot-line', title: 'AI-Powered', desc: 'Intelligent features that work for you 24/7' },
                    { icon: 'ri-money-dollar-circle-line', title: 'Affordable', desc: 'Packages starting at just $1,000' },
                    { icon: 'ri-customer-service-2-line', title: 'Dedicated Support', desc: 'Ongoing updates and management available' },
                    { icon: 'ri-shield-star-line', title: 'Secure & Reliable', desc: 'Enterprise-grade security for your website' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start space-x-4 group">
                      <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-500/30 transition-colors">
                        <i className={`${item.icon} text-indigo-400 text-lg`}></i>
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-sm">{item.title}</h4>
                        <p className="text-slate-400 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Card */}
              <div className="bg-gradient-to-br from-indigo-600/20 to-blue-600/20 backdrop-blur-xl rounded-2xl p-8 border border-indigo-500/30">
                <div className="text-center">
                  <div className="w-16 h-16 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-chat-voice-line text-indigo-400 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 font-space">Prefer to Talk?</h3>
                  <p className="text-slate-300 text-sm mb-6">
                    Click the chat button in the bottom-right corner to speak with our AI assistant instantly, or call us directly.
                  </p>
                  <a
                    href="tel:+17169395685"
                    className="inline-flex items-center px-6 py-3 bg-indigo-500 text-white rounded-lg font-semibold hover:bg-indigo-600 transition-all duration-300 shadow-lg shadow-indigo-500/25 whitespace-nowrap cursor-pointer hover:-translate-y-0.5"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    (716) 939-5685
                  </a>
                </div>
              </div>

              {/* Process Quick View */}
              <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50">
                <h3 className="text-xl font-bold text-white mb-5 font-space">
                  How It <span className="text-indigo-400">Works</span>
                </h3>
                <div className="space-y-4">
                  {[
                    { step: '01', text: 'Fill out the form or book a consultation' },
                    { step: '02', text: 'We create a mock website in 3-5 days' },
                    { step: '03', text: 'Approve the design and we build it out' },
                    { step: '04', text: 'Launch your site and go live!' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <span className="text-indigo-400 font-bold text-lg font-space w-8 flex-shrink-0">
                        {item.step}
                      </span>
                      <div className="h-px flex-1 bg-slate-700/50"></div>
                      <span className="text-slate-300 text-sm">{item.text}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/services"
                  className="mt-6 inline-flex items-center text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors cursor-pointer"
                >
                  View our packages <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Calendar Section */}
      <section className="py-20 bg-slate-900 relative z-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div
            id="booking-header"
            data-animate
            className={getAnimationClass('booking-header', 'text-center mb-12')}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-space">
              Schedule a <span className="text-indigo-400">Free Consultation</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Book a time that works for you. We&apos;ll discuss your website needs and how ClearPath Intelligence can help your business succeed online.
            </p>
          </div>

          <div
            id="booking-calendar"
            data-animate
            className={getAnimationClass('booking-calendar', '')}
            style={{ animationDelay: '0.2s' }}
          >
            <BookingCalendar
              timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/3b447b29-28b9-4082-8452-d9675d64c27c.41e3f611565ee1afe9d08ae1e827178dc5ef233faba79b09449fb9a32c278885"
              appointmentsApi="https://readdy.ai/api/public/calendar/appointments/3b447b29-28b9-4082-8452-d9675d64c27c.41e3f611565ee1afe9d08ae1e827178dc5ef233faba79b09449fb9a32c278885"
              texts={{
                selectDate: 'Select Date',
                selectTime: 'Select Time',
                reviewAndBook: 'Review & Book',
                previousMonth: 'Previous',
                nextMonth: 'Next',
                noSlotsAvailable: 'No time slots available for this date',
                yourName: 'Your Name',
                phoneNumber: 'Phone Number',
                selectedDate: 'Date',
                selectedTime: 'Time',
                modifyTime: 'Change',
                confirmBooking: 'Confirm Booking',
                bookingSuccess: 'Your consultation has been booked successfully!',
                bookingError: 'Failed to book. Please try again.',
                back: 'Back',
                loading: 'Loading...',
                sun: 'Sun',
                mon: 'Mon',
                tue: 'Tue',
                wed: 'Wed',
                thu: 'Thu',
                fri: 'Fri',
                sat: 'Sat'
              }}
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
